import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TypeAnimation } from 'react-type-animation';
import { ChevronDown, Sparkles } from 'lucide-react';

const SDE_TAGLINES = [
  'Building scalable systems that ship.',
  'Turning ideas into production-ready code.',
  'Engineering full-stack solutions with precision.',
  'Designing architectures that scale effortlessly.',
  'Crafting reliable software from code to cloud.',
];

const typeSequence = SDE_TAGLINES.flatMap((line) => [line, 2200]);

const StaggeredWord = ({ word, delay = 0 }) => (
  <span className="inline-flex">
    {word.split('').map((char, i) => (
      <motion.span
        key={`${word}-${i}`}
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          delay: delay + i * 0.05,
          duration: 0.5,
          ease: [0.22, 1, 0.36, 1],
        }}
        className="inline-block"
      >
        {char}
      </motion.span>
    ))}
  </span>
);

const NameIntro = ({ onComplete }) => {
  const [visible, setVisible] = useState(true);

  const dismiss = useCallback(() => {
    setVisible(false);
    setTimeout(onComplete, 600);
  }, [onComplete]);

  useEffect(() => {
    const timer = setTimeout(dismiss, 4500);
    return () => clearTimeout(timer);
  }, [dismiss]);

  useEffect(() => {
    if (visible) document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, [visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.6, ease: 'easeInOut' }}
          className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-white cursor-pointer overflow-hidden"
          onClick={dismiss}
          role="dialog"
          aria-label="Welcome to Karthik Gouda portfolio"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 opacity-[0.35]"
            style={{
              backgroundImage: 'radial-gradient(circle, #cbd5e1 1px, transparent 1px)',
              backgroundSize: '28px 28px',
            }}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2 }}
            className="absolute top-1/4 left-1/2 -translate-x-1/2 w-80 h-80 bg-secondary/10 rounded-full blur-3xl"
          />
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.4, delay: 0.2 }}
            className="absolute bottom-1/4 right-1/4 w-56 h-56 bg-primary/5 rounded-full blur-3xl"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: [0, 0.15, 0.08], scale: [0.6, 1.1, 1] }}
            transition={{ duration: 2, delay: 0.6, ease: 'easeOut' }}
            className="absolute w-64 h-64 sm:w-80 sm:h-80 rounded-full border border-secondary/20"
          />

          <div className="relative z-10 text-center px-6 max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-gray-100 bg-slate-50 mb-10 shadow-sm"
            >
              <Sparkles size={14} className="text-secondary" />
              <span className="text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase text-gray-500">
                Software Development Engineer
              </span>
            </motion.div>

            <h1 className="text-5xl sm:text-7xl md:text-8xl lg:text-9xl font-display font-black leading-[0.95] tracking-tighter mb-8">
              <span className="block text-primary">
                <StaggeredWord word="Karthik" delay={0.45} />
              </span>
              <span className="block text-secondary mt-1">
                <StaggeredWord word="Gouda" delay={0.85} />
              </span>
            </h1>

            <motion.div
              initial={{ scaleX: 0, opacity: 0 }}
              animate={{ scaleX: 1, opacity: 1 }}
              transition={{ delay: 1.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="h-px w-24 sm:w-32 bg-gradient-to-r from-transparent via-secondary to-transparent mx-auto mb-8 origin-center"
            />

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3, duration: 0.6 }}
              className="text-base sm:text-lg md:text-xl text-gray-500 font-medium min-h-[2.5rem] flex items-center justify-center"
            >
              <TypeAnimation
                sequence={typeSequence}
                wrapper="span"
                speed={45}
                repeat={Infinity}
                className="text-secondary italic font-semibold"
              />
            </motion.div>
          </div>

          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.7, duration: 0.6 }}
            onClick={(e) => { e.stopPropagation(); dismiss(); }}
            className="absolute bottom-12 flex flex-col items-center gap-2 text-gray-400 hover:text-secondary transition-colors group"
            aria-label="Continue to portfolio"
          >
            <span className="text-[10px] font-bold uppercase tracking-[0.3em] group-hover:tracking-[0.35em] transition-all">
              Enter
            </span>
            <motion.div
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
              className="p-2 rounded-full border border-gray-200 group-hover:border-secondary/50 transition-colors"
            >
              <ChevronDown size={18} />
            </motion.div>
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NameIntro;